import React from 'react';
import { FlatList, FlatListProps } from 'react-native';
import {
  ListItem,
  Props as ListItemProps,
  ListItemSeparator,
  ListItemStyleType,
  ListItemTextStyleType,
  ListOption,
  SwipeableListItemProps,
} from './list-item';

// renderItem is marked as required by FlatListProps. Exclude it because we define our own
type ModifiedFlatListType<TListOption> = Pick<
  FlatListProps<TListOption>,
  Exclude<keyof FlatListProps<TListOption>, 'renderItem'>
>;

export type Props<TListOption> = ModifiedFlatListType<TListOption> & {
  leftComponentStyle?: ListItemProps<TListOption>['leftComponentStyle'];
  listItemStyle?: ListItemStyleType<TListOption>;
  listItemTitleStyle?: ListItemTextStyleType<TListOption>;
  listRef?: React.Ref<FlatList>;
  onSelect?: (option: TListOption) => void;
  renderLeftComponent?: (option: TListOption) => React.ReactNode;
  renderListItemFooter?: (option: TListOption) => React.ReactNode;
  renderMainContent?: (option: TListOption) => React.ReactNode;
  renderRightComponent?: (option: TListOption) => React.ReactNode;
  rightComponentStyle?: ListItemProps<TListOption>['rightComponentStyle'];
  swipeableProps?: SwipeableListItemProps<TListOption>;
};

const defaultKeyExtractor = ({ value }: ListOption) => String(value);

export const List = <TListOption extends ListOption = ListOption>({
  alwaysBounceVertical = false,
  ItemSeparatorComponent = ListItemSeparator,
  keyExtractor = defaultKeyExtractor,
  leftComponentStyle,
  listItemStyle,
  listItemTitleStyle,
  listRef,
  onSelect,
  renderLeftComponent,
  renderListItemFooter,
  renderMainContent,
  renderRightComponent,
  rightComponentStyle,
  style = {},
  swipeableProps,
  ...rest
}: Props<TListOption>) => {
  const sharedProps: FlatListProps<TListOption> = {
    alwaysBounceVertical,
    ItemSeparatorComponent,
    keyExtractor,
    ...rest,
    renderItem: ({ item, index }: { index: number; item: TListOption }) => (
      <ListItem<TListOption>
        item={item}
        leftComponentStyle={leftComponentStyle}
        listItemStyle={listItemStyle}
        listTitleStyle={listItemTitleStyle}
        onSelect={onSelect}
        renderLeftComponent={renderLeftComponent}
        renderListItemFooter={renderListItemFooter}
        renderMainContent={renderMainContent}
        renderRightComponent={renderRightComponent}
        rightComponentStyle={rightComponentStyle}
        swipeableProps={swipeableProps}
        testID={`list-item-${index}`}
      />
    ),
    scrollIndicatorInsets: {
      right: 1,
    },
    style,
  };

  return <FlatList<TListOption> {...sharedProps} ref={listRef} />;
};
